import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import { AccordionItem } from '@mm/components';
import { GoogleSheetsConnections } from '@mm/state/modules/connectionsTableState/selectors';
import { selectGoogleAccounts } from '@mm/state/selectors/googleSheetSelectors';
import { useGoogleSheetsCRUDActions } from '@mm/state/actions/GoogleSheetsCRUDActions';
import './IntegrationBaseComponent.scss';
import { HeaderComponent } from './HeaderComponent';
import {
  Grid,
  GridLabels,
  GridRow,
} from '../../../../../../../../common/components/Grid/Grid';
import {
  ComponentDataItem,
  DefaultCell,
} from '../../../../../../contacts/components/ContactsTable/ContactTable';
import { TableLabel } from '../../../../../../../../common/components/TableGrid/TableGrid';
import { PrimaryButton } from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { ReactComponent as IntegrationsBacksplash } from 'assets/feature-svgs/integrations.svg';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { landmineNames } from '@mm/constants/landmine-names';
import { PageBillingType } from '@mm/state/interfaces/connected-page';

type Props = {
  isAccountConnected: boolean;
  onDisconnect: (id: number) => void;
  spreadsheetCount: number;
  cellData: ComponentDataItem[];
  googleSheetsConnections: GoogleSheetsConnections[];
  className?: string;
  name?: string;
};

export const IntegrationBaseComponent = (props: Props) => {
  const {
    isAccountConnected,
    className,
    onDisconnect,
    spreadsheetCount,
    cellData,
    googleSheetsConnections,
    name,
  } = props;

  const integrationsClassname = classnames('integrations-component', className);
  const accounts = useSelector(selectGoogleAccounts);
  const { getOauthUrl } = useGoogleSheetsCRUDActions();
  const { openUniversalUpgradeLandmineModal } = useModalActions();
  const gridTemplateColumns = `20% 20% 20% 15% 15% 10%`;

  const [oauthUrl, setOauthUrl] = useState('');

  useEffect(() => {
    const getLink = async () => {
      const link = await getOauthUrl(window.location.href);
      if (link) {
        setOauthUrl(link);
      }
    };
    getLink();
  }, []);

  const headerComponent = (
    <HeaderComponent
      accounts={accounts}
      isAccountConnected={isAccountConnected}
      spreadsheetCount={spreadsheetCount}
      accountsCount={accounts.length}
      oauthUrl={oauthUrl}
      showUpgradeModal={() =>
        openUniversalUpgradeLandmineModal({
          feature: 'nativeIntegrations',
          planFallback: PageBillingType.ProUnicorn,
          landmineName: landmineNames.INTEGRATIONS_NATIVE_INTEGRATIONS,
        })
      }
      name={name}
    />
  );

  return (
    <div className={integrationsClassname}>
      {isAccountConnected ? (
        <AccordionItem
          className="integrations-item"
          caretStyleIcon
          closeOnOutsideClick={false}
          titleComponent={headerComponent}
        >
          {!googleSheetsConnections.length && (
            <div className="integrations-empty-backspalsh">
              <IntegrationsBacksplash />
              <div className="integrations-empty-backspalsh-cta">
                There are no connections yet
              </div>
            </div>
          )}
          {googleSheetsConnections.map((connection, connectionIdx) => (
            <Grid
              className="integrations-grid"
              key={`google-connection-${connectionIdx}`}
            >
              <div className="integrations-grid__account-header">
                <div className="integrations-grid__account-info">
                  Google Account:{' '}
                  <span className="integrations-grid___account-name">
                    {connection.accountEmail
                      ? connection.accountEmail
                      : 'Not Assigned'}{' '}
                  </span>
                </div>
                {accounts.find(
                  acc => acc.id === connection.accountId && !acc.readOnly,
                ) && (
                  <PrimaryButton
                    className="integrations__header-add-button-secondary"
                    onClick={() =>
                      onDisconnect(
                        Number(
                          accounts.find(acc => acc.id === connection.accountId)
                            ?.id,
                        ),
                      )
                    }
                    color="secondary-blue"
                  >
                    Disconnect
                  </PrimaryButton>
                )}
              </div>
              {!!connection.connections.length && (
                <>
                  <GridLabels
                    className="integrations-grid__labels"
                    gridTemplateColumns={gridTemplateColumns}
                  >
                    {cellData.map(cell => (
                      <TableLabel
                        key={(cell.keyName as string) || cell.label}
                        dataItem={cell}
                        orderByAtTheEnd
                      />
                    ))}
                  </GridLabels>

                  <div className="integrations-grid__rows-container">
                    {connection.connections.map(item => (
                      <GridRow
                        key={item.id}
                        gridTemplateColumns={gridTemplateColumns}
                      >
                        {cellData.map(({ Cell, keyName }) => {
                          return Cell ? (
                            <Cell key={keyName} item={item} />
                          ) : (
                            <DefaultCell
                              keyName={keyName as string}
                              item={item}
                            />
                          );
                        })}
                      </GridRow>
                    ))}
                  </div>
                </>
              )}
            </Grid>
          ))}
        </AccordionItem>
      ) : (
        headerComponent
      )}
    </div>
  );
};
